import React, { useState, useEffect } from 'react';
import {
  Grid,
  Container,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Pagination,
  Skeleton,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Fab,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material';

import { ShopItem, ShopFilters } from '../../types/shop';
import { useNavigate } from 'react-router-dom';
import { isFeatureEnabled } from '../../utils/config';
import ComingSoon from '../../Components/ComingSoon';

// Mock data for demonstration
const mockItems: ShopItem[] = [
  {
    id: '1',
    name: 'PEO Foundation T-Shirt',
    description: 'Comfortable cotton t-shirt featuring the PEO Foundation logo',
    shortDescription: 'PEO Foundation branded t-shirt',
    price: 25.0,
    category: 'Clothing',
    tags: ['clothing', 'foundation', 'branded'],
    images: [
      'https://prd.place/400/300?id=1',
      'https://prd.place/400/300?id=2',
    ],
    thumbnail: 'https://prd.place/400/300?id=1',
    inStock: true,
    variants: [
      {
        id: '1-1',
        itemId: '1',
        name: 'Small',
        sku: 'TS-S',
        price: 25.0,
        inStock: true,
        attributes: { size: 'S' },
      },
      {
        id: '1-2',
        itemId: '1',
        name: 'Medium',
        sku: 'TS-M',
        price: 25.0,
        inStock: true,
        attributes: { size: 'M' },
      },
      {
        id: '1-3',
        itemId: '1',
        name: 'Large',
        sku: 'TS-L',
        price: 25.0,
        inStock: true,
        attributes: { size: 'L' },
      },
    ],
    featured: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '2',
    name: 'PEO Chapter Meeting Notebook',
    description: 'Professional notebook for chapter meetings and notes',
    shortDescription: 'Professional meeting notebook',
    price: 15.0,
    category: 'Office Supplies',
    tags: ['office', 'notebook', 'meetings'],
    images: ['https://prd.place/400/300?id=3'],
    thumbnail: 'https://prd.place/400/300?id=3',
    inStock: true,
    variants: [
      {
        id: '2-1',
        itemId: '2',
        name: 'Standard',
        sku: 'NB-STD',
        price: 15.0,
        inStock: true,
        attributes: { type: 'standard' },
      },
    ],
    featured: false,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: '3',
    name: 'PEO Foundation Mug',
    description: 'Ceramic coffee mug with PEO Foundation design',
    shortDescription: 'PEO Foundation coffee mug',
    price: 12.0,
    category: 'Home & Garden',
    tags: ['home', 'mug', 'foundation'],
    images: ['https://prd.place/400/300?id=4'],
    thumbnail: 'https://prd.place/400/300?id=4',
    inStock: true,
    variants: [
      {
        id: '3-1',
        itemId: '3',
        name: 'Standard',
        sku: 'MG-STD',
        price: 12.0,
        inStock: true,
        attributes: { type: 'standard' },
      },
    ],
    featured: true,
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-10'),
  },
];

const mockCategories = [
  {
    id: '1',
    name: 'Clothing',
    description: 'PEO branded clothing items',
    image: 'https://prd.place/400x300?id=5',
    itemCount: 5,
  },
  {
    id: '2',
    name: 'Office Supplies',
    description: 'Office and meeting supplies',
    image: 'https://prd.place/400x300?id=6',
    itemCount: 3,
  },
  {
    id: '3',
    name: 'Home & Garden',
    description: 'Home decor and garden items',
    image: 'https://prd.place/400x300?id=7',
    itemCount: 2,
  },
];

export default function Shop() {
  const navigate = useNavigate();

  // Mock shop context since ShopContext.tsx is empty
  const mockShopContext = {
    state: {
      loading: false,
      filters: {} as ShopFilters,
      cart: { items: [], itemCount: 0 },
    },
    addToCart: (item: ShopItem) => {},
    setFilters: (filters: Partial<ShopFilters>) => {},
  };
  const { state, addToCart, setFilters } = mockShopContext;

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    // Simulate loading data
    setTimeout(() => {
      // In a real app, this would be an API call
      setFilters({});
    }, 1000);
  }, [setFilters]);

  // Check if shop feature is enabled - moved after hooks
  if (!isFeatureEnabled('SHOP_ENABLED')) {
    return (
      <Grid container>
        <Grid item xs={12} textAlign="center" sx={{ p: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Coming Soon the shop will be available here. For now, you can order
            from the following sources:
          </Typography>
        </Grid>
        <Grid item xs={12} textAlign="center" sx={{ p: 1 }}>
          <Link
            href="https://eorders.peointernational.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Local and S/P/D Chapter Supplies and Emblem Ordering
            </Typography>
          </Link>
        </Grid>
        <Grid item xs={12} textAlign="center" sx={{ p: 1 }}>
          <Link
            href="https://www.peointernational.org/resources/fundraising-marketplace/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              P.E.O. International Fundraising Marketplace
            </Typography>
          </Link>
        </Grid>
        <Grid item xs={12} textAlign="center" sx={{ p: 1 }}>
          <Link
            href="https://www.hjgreek.com/index.cfm?event=Products.showCategories&Organization_ID=56"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Herff-Jones (Jewelry)
            </Typography>
          </Link>
        </Grid>
        <Grid item xs={12} textAlign="center" sx={{ p: 1 }}>
          <ComingSoon />
        </Grid>
      </Grid>
    );
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setFilters({ search: event.target.value });
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setFilters({ category: category === 'all' ? undefined : category });
  };

  const handleSortChange = (sortBy: string) => {
    setSortBy(sortBy);
    setFilters({ sortBy: sortBy as any });
  };

  const handleAddToCart = (item: ShopItem) => {
    addToCart(item);
    // Navigate to cart after adding item
    navigate('/shop/cart');
  };

  const handleItemClick = (item: ShopItem) => {
    navigate(`/shop/item/${item.id}`);
  };

  const handleCartClick = () => {
    navigate('/shop/cart');
  };

  const toggleFavorite = (itemId: string) => {
    setFavorites((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const filteredItems = mockItems.filter((item) => {
    if (
      searchTerm &&
      !item.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }
    if (selectedCategory !== 'all' && item.category !== selectedCategory) {
      return false;
    }
    return true;
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'price':
        return a.price - b.price;
      case 'newest':
        return b.createdAt.getTime() - a.createdAt.getTime();
      default:
        return 0;
    }
  });

  const paginatedItems = sortedItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

  if (state.loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" height={24} />
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="text" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      {/* Modern Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          py: 6,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            PEO Shop
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 600 }}>
            Discover PEO branded merchandise and chapter supplies
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 6 }}>
        {/* Search and Filters Bar */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 3,
            p: 3,
            mb: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search items..."
                value={searchTerm}
                onChange={handleSearch}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {mockCategories.map((category) => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => handleSortChange(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="price">Price</MenuItem>
                  <MenuItem value="newest">Newest</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
                size="large"
                onClick={() => setFiltersOpen(true)}
                sx={{ borderRadius: 2 }}
              >
                Filters
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Results Count */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {filteredItems.length} of {mockItems.length} items
          </Typography>
          <Button
            variant="text"
            color="primary"
            onClick={() => setFiltersOpen(true)}
            sx={{ display: { xs: 'block', md: 'none' } }}
          >
            <FilterIcon sx={{ mr: 1 }} />
            Filters
          </Button>
        </Box>

        {/* Items Grid */}
        <Grid container spacing={3}>
          {paginatedItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="250"
                    image={item.thumbnail}
                    alt={item.name}
                    sx={{ objectFit: 'cover' }}
                    onClick={() => handleItemClick(item)}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(item.id);
                    }}
                  >
                    {favorites.includes(item.id) ? (
                      <FavoriteIcon color="error" />
                    ) : (
                      <FavoriteBorderIcon />
                    )}
                  </IconButton>
                  {item.featured && (
                    <Chip
                      label="Featured"
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        backgroundColor: 'primary.main',
                        color: 'white',
                      }}
                    />
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    noWrap
                    onClick={() => handleItemClick(item)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, lineHeight: 1.5 }}
                  >
                    {item.shortDescription}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    >
                      ${item.price.toFixed(2)}
                    </Typography>
                    {item.salePrice && (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        ${item.salePrice.toFixed(2)}
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}
                  >
                    {item.tags.slice(0, 2).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    size="medium"
                    variant="contained"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(item);
                    }}
                    disabled={!item.inStock}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      py: 1,
                    }}
                  >
                    {item.inStock ? 'Add to Cart' : 'Out of Stock'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              size="large"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        )}

        {/* No Results */}
        {filteredItems.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No items found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Try adjusting your search criteria or filters
            </Typography>
          </Box>
        )}
      </Container>

      {/* Filters Drawer */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 350 } },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setFiltersOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle1" gutterBottom>
            Categories
          </Typography>
          <List>
            <ListItem button onClick={() => handleCategoryChange('all')}>
              <ListItemText primary="All Categories" />
            </ListItem>
            {mockCategories.map((category) => (
              <ListItem
                key={category.id}
                button
                onClick={() => handleCategoryChange(category.name)}
              >
                <ListItemText primary={category.name} />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" gutterBottom>
            Sort By
          </Typography>
          <List>
            {['name', 'price', 'newest'].map((sort) => (
              <ListItem
                key={sort}
                button
                onClick={() => handleSortChange(sort)}
              >
                <ListItemText
                  primary={sort.charAt(0).toUpperCase() + sort.slice(1)}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Floating Cart Button */}
      <Fab
        color="primary"
        aria-label="cart"
        onClick={handleCartClick}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <Badge badgeContent={state.cart.itemCount} color="error">
          <CartIcon />
        </Badge>
      </Fab>
    </Box>
  );
}
